<template>
  <div class="MissionMapboxComponent">
    <v-row>
      <v-col cols="4">
        <template v-if="drawingInProgress">
          <!-- <v-select label="Code Type" :items="['CPIT']" value="CPIT" disabled /> -->
          <v-select v-model="form.codeType" label="Code Type" :items="assetTypes" item-value="code" item-text="name" />
        </template>
        <template v-else>
          <div
            style="height:100%;display:flex;flex-direction:column;justify-content:space-between"
          >
            <div
              v-for="(asset, i) in assetsAsButtons"
              :key="asset.asset_id"
              style="display:flex;height:30%"
            >
              <v-btn
                block
                x-large
                :id="`asset_marker_button_${i}`"
                :style="{ height: '100%', backgroundColor: asset.button_bg_color }"
                @click="selectAsset(asset)"
                v-html="asset.button_inner_html"
              ></v-btn>
            </div>
          </div>
        </template>
      </v-col>
      <v-col>
        <div class="mapbox-container">
          <!-- Tried out using an overlay with an icon (truck) fixed in the center of the map. -->
          <!-- (involved disabling the mapbox location icon to avoid having two indicators) -->
          <!-- <div v-if=" ! isTrackingUserLocation" class="mapbox-center-marker"></div> -->
          <mapbox-map ref="mapbox"
            :height="mapHeight"
            :geolocate-control-options="{ enabled: true }"
            :custom-marker-options="customMarkerOptions"
            :draw-control-options="{ enabled: true, controls: { point: true, trash: false } }"
            :interactions="{ scrollZoom: false, touchZoomRotate: false, keyboard: false, doubleClickZoom: false }"
            @trackuserlocationend="onUserTrackingChange(false)"
            @trackuserlocationstart="onUserTrackingChange(true)"
            @draw-create="onDrawingCreated"
            @draw-update="onDrawingUpdated"
            @draw-delete="onDrawingDeleted"
            @draw-selectionchange="onDrawingSelectionChanged"
            @draw-modechange="onDrawingModeChanged"
            @reference-point="onNewReferencePoint"
            @user-location="onNewUserLocation"
            @map-styledata="onMapStyleChange"
          >
          </mapbox-map>
        </div>
      </v-col>
    </v-row>
    <v-row ref="footer">
      <v-col cols="4">
        <div class="row">
          <template v-if="drawingInProgress">
            <div class="col col-6">
              <v-btn primary large block @click="cancelDrawing">
                CANCEL
              </v-btn>
            </div>
            <div class="col col-6">
              <v-btn primary large block :loading="creatingAsset" :disabled="creatingAsset || form.nodes.length === 0" @click="createAsset">
                CREATE
              </v-btn>
            </div>
          </template>
          <template v-else>
            <slot name="button-footer" />
          </template>

        </div>
      </v-col>
      <v-col>
        <slot name="map-footer" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { AssetService } from "@/services/asset.service";
import { MissionAssetService } from "@/services/missionAsset.service";
import { denormalize } from "@/helpers/JsonApiDenormalizer";
import MapboxMap from "@/components/Mapbox/Mapbox.vue";
import * as turf from "@turf/turf";
import _ from "lodash";

export default {
  props: {
    mission: {
      type: Object
    },
    height: {
      type: [Number, String],
      default: 500
    },
    filterGeojson: {
      type: Function,
      required: false,
    },
    decorateGeojson: {
      type: Function,
      required: false,
    },
    pickAssetIdsForButtons: {
      type: Function,
      required: false,
    }
  },
  components: {
    MapboxMap,
  },
  data: () => ({
    isMounted: false, // useful to stop recursive debounced methods from calling themselves
    assetsGeojson: {},
    lastUserLocation: null,  // min: lat and lng properties
    lastReferencePoint: null,  // min: lat and lng properties
    centerDistanceMovedRecently: 0, // meters. resets once over threshold.
    isTrackingUserLocation: false,
    defaultCenter: [174.762192, -36.84845],
    mapHeight: 300,
    lastServiceDataLimit: 9,
    assetButtonCountLimit: 3,
    lastServiceDataByCoreId: {}, // indexed by coreId so we can tell if no service data found vs never fetched
    lastServiceDataByCoreIdBusyFetching: false,
    distance: 0.2,
    form: {
      codeType: "CPIT",
      codeId: 180,
      geometryType: "",
      nodes: []
    },
    creatingAsset: false,
    drawingInProgress: false,
    colors: {
        "default":   'hsl(0, 0%, 96%)',
        "nearby-0":  'hsl(199, 98%, 48%)',
        "nearby-1":  'hsl(262, 52%, 47%)',
        "nearby-2":  'hsl(291, 96%, 62%)',
        "nearby-x":  'hsl(0, 0%, 96%)',
        "success-0": 'hsl(122, 39%, 49%)',
        "success-1": 'hsl(122, 37%, 84%)',
        "success-2": 'hsl(125, 39%, 94%)',
        "success-x": 'hsl(0, 0%, 96%)',
        "danger-0":  'hsl(4, 90%, 58%)',
        "danger-1":  'hsl(354, 100%, 90%)',
        "danger-2":  'hsl(351, 100%, 96%)',
        "danger-x":  'hsl(0, 0%, 96%)',
      },
  }),
  computed: {
    ...mapGetters("config", ["getConfigByKey"]),
    assetTypes() {
      return this.getConfigByKey("asset_types") || [];
    },
    customMarkerOptions() {
      // Disabled. Using built-in location markers.
      return null;
      /*
      let classes = [];
      if (this.isTrackingUserLocation) {
        classes.push('mapbox-truck-marker'); // truck icon as background
        classes.push('is-tracking'); // the pinging location circle around truck
      }
      return classes.length ? { classes, coordinates: this.lastReferencePoint } : null;
      */
    },
    assetIdsInScopeForLastServiceData() {
      let ids = [];
      const features = this.cpitGeojsonSortedByProximity && this.cpitGeojsonSortedByProximity.features
        ? this.cpitGeojsonSortedByProximity.features
        : [];
      const featuresNearest = features.slice(0, this.lastServiceDataLimit);
      featuresNearest.forEach(f => {
        ids.push(f.properties.asset_id);
      });
      return ids;
    },
    assetsAsButtons() {
      let assets = [];
      let ids = this.pickAssetIdsForButtons
        ? this.pickAssetIdsForButtons(this.cpitGeojsonSortedByProximity, this.assetButtonCountLimit)
        : this.assetIdsInScopeForLastServiceData;
      if (this.cpitGeojsonSortedByProximity && this.cpitGeojsonSortedByProximity.features) {
        this.cpitGeojsonSortedByProximity.features.forEach(f => {
          if (ids.includes(f.properties.asset_id)) {
            assets.push(Object.assign({}, f.properties));
          }
        });
      }
      return assets.slice(0, this.assetButtonCountLimit);
    },
    cpitGeojsonSortedByProximity() {
      const vm = this;
      if ( ! _.isEmpty(vm.assetsGeojson)) {
        let features = []; // this will be sorted
        // NOTE: We deep clone assetsGeojson in order to not affect nested properties via object references.
        let geojson = _.cloneDeep(vm.assetsGeojson);
        if (this.filterGeojson) {
          geojson = this.filterGeojson(geojson);
        }
        // We need to do some calculations to determine assets closest to center.
        let center = vm.defaultCenter;
        if (vm.isTrackingUserLocation) {
          center = vm.lastUserLocation
            ? [vm.lastUserLocation.lng, vm.lastUserLocation.lat]
            : center;
        }
        else {
          center = vm.lastReferencePoint
            ? [vm.lastReferencePoint.lng, vm.lastReferencePoint.lat]
            : center;
        }
        _.get(geojson, 'features', []).forEach((feature, i) => {
          const coordinates = feature.geometry.coordinates;
          const to = turf.point([coordinates[0], coordinates[1]]);
          const from = turf.point(center);
          const distance = turf.distance(from, to) * 1000; // km => m
          feature.properties.distance = distance;
          features.push(feature);
        });
        features = features.sort((a, b) => {
          return a.properties.distance - b.properties.distance; // shortcut since we're sorting numbers
        });
        // Finally, we need to add a proximity property to our geojson features
        // so that we can use it to change marker color when rendering layers.
        features.forEach((f, i) => {
          f.properties.proximity = i+1;
        });
        // We want to convert the CPIT points to polygons so that we can
        // tell mapbox to render them as squares (not small circles).
        features.forEach((feature, i) => {
          const coordinates = feature.geometry.coordinates;
          // Doing this, we end up with rotated square (diamond) shapes
          // (so opting to use manual padding approach further below)
          // const radius = 0.001
          // const options = { steps: 4, units: 'kilometers', properties: feature.properties };
          // const polygon = turf.circle(coordinates, radius, options);
          // features.push(polygon);
          const paddingX = 0.000025;
          const paddingY = 0.000020;
          let square_coords = [[
            [coordinates[0] - paddingX, coordinates[1] - paddingY],
            [coordinates[0] + paddingX, coordinates[1] - paddingY],
            [coordinates[0] + paddingX, coordinates[1] + paddingY],
            [coordinates[0] - paddingX, coordinates[1] + paddingY],
            [coordinates[0] - paddingX, coordinates[1] - paddingY]
          ]];
          feature.geometry.type = 'Polygon';
          feature.geometry.coordinates = square_coords;
          features[i] = feature;
        });
        geojson.features = features;
        // Last chance for parent component to modify geojson
        // (added this so mission_asset info could be used)
        if (this.decorateGeojson) {
          geojson = this.decorateGeojson(geojson);
        }
        // Now add/prepare button-specific data to each asset. We limit to a number that
        // includes how many buttons we'd want to pre-load any async button data for.
        let asset_ids_for_buttons = [];
        if (this.pickAssetIdsForButtons) {
          asset_ids_for_buttons = this.pickAssetIdsForButtons(geojson, this.lastServiceDataLimit);
        }
        else {
          for (const f of features) {
            const asset_id = parseInt(f.properties.asset_id, 10);
            if (asset_id) {
              asset_ids_for_buttons.push(asset_id);
            }
            if (asset_ids_for_buttons.length >= this.assetButtonCountLimit) {
              break;
            }
          }
        }
        const show_last_service_date = this.getConfigByKey('mission_map.asset_buttons_show_last_service_date');
        features.forEach((f, i) => {

          let button_bg_color;
          let button_inner_html;
          const asset = f.properties;
          const asset_id = parseInt(asset.asset_id, 10);

          // Only bother with the items that are relevant for buttons.
          // Otherwise we'd needlessly process many, many assets.
          if (asset_ids_for_buttons.includes(asset_id)) {

            if (show_last_service_date && ! this.lastServiceDataByCoreId.hasOwnProperty(asset_id)) {
              this.getLastServiceDataByCoreId(asset_id);
            }

            // NOTE: Button background colour determined from marker profile added by decorateGeojson above
            let marker_profile = 'default';
            marker_profile = _.get(f, 'properties.marker_profile', marker_profile) || marker_profile;
            button_bg_color = this.getColorByProfile(marker_profile);

            // Button inner HTML may use data from other objects that gets populated asynchronously
            button_inner_html = this.lastServiceDataByCoreId.hasOwnProperty(asset_id)
              ? this.getAssetButtonInnerHtml(asset)
              : button_inner_html;
          }

          features[i].properties.button_bg_color = button_bg_color;
          features[i].properties.button_inner_html = button_inner_html;
        });
        return Object.freeze(geojson);
      }
      return null;
    },
    assetsDisabled() {
      if (this.mission && this.mission.missionAssets) {
        return this.mission.missionAssets.map(asset => asset.coreId);
      }
      return [];
    },
    // Draw starts when user clicks on point control button
    // drawingInProgress() {
    //   return this.form.nodes.length > 0;
    // }
  },
  created() {
    this.setMapHeight();
  },
  watch: {
    height() {
      this.setMapHeight();
    },
    lastUserLocation(value, prev) {
      // Generally we are more interested in the "lastReferencePoint" which
      // could be the center of the map when a user is manually dragging
      // the map around, or whenever location tracking is in an active
      // state and constantly keeping the user centered on the map.
      // However, if user location tracking is in passive state,
      // we still need to update layers so that the "closest"
      // markers are still updated as users drive around.
      this.updateMapLayers();
    },
    lastReferencePoint(value, prev) {
      if (value && value.lat) {
        if ( ! prev) {
          this.fetchAssetsForMovement();
        }
        else if (prev && prev.lat) {
          // We'll only fetch assets if the user has moved more than X meters since last fetch.
          // @todo - better way is to fetch assets once user reaches bounds of current data.
          const to = turf.point([prev.lng, prev.lat]);
          const from = turf.point([value.lng, value.lat]);
          const distance = turf.distance(from, to) * 1000; // km => m
          const cumulativeDistance = this.centerDistanceMovedRecently + distance;
          if (cumulativeDistance >= 100) {
            this.fetchAssetsForMovement();
            this.centerDistanceMovedRecently = 0;
          }
          else {
            this.centerDistanceMovedRecently = cumulativeDistance;
          }
        }
      }
      this.updateMapLayers();
    },
    assetsGeojson(geojson, prev) {
      this.updateMapLayersNow();
    },
    cpitGeojsonSortedByProximity() {
      this.updateMapLayersNow();
    }
  },
  mounted() {
    this.isMounted = true;
  },
  beforeDestroy() {
    this.isMounted = false;
  },
  methods: {
    async createAsset() {
      this.creatingAsset = true;
      try {
        const data = await AssetService.create({
          codeId: this.form.codeId,
          assetNodes: this.form.nodes,
          geometryType: this.form.geometryType,
          comment: "Created during a mission.",
          transformer: 'asset_geojson',
        }).then(response => response.data);
        this.cancelDrawing();
        this.fetchAssetsAroundCenter()
        this.selectAsset(data.features[0].properties, true);
      } catch (err) {
        console.log(err);
        this.creatingAsset = false;
      }
      this.creatingAsset = false;
    },
    fetchAssetsAroundCenter() {
      const vm = this;

      const codeIds = vm.assetTypes.map(
        (assetType) => assetType.third_party_code
      );

      return AssetService.geojson({
          centerY: vm.lastReferencePoint.lat,
          centerX: vm.lastReferencePoint.lng,
          outerRad: vm.distance + 0.2,
          // codeIds: [180],
          codeIds: codeIds,
          managed_by_firm_id:
          vm.mission && vm.mission.inspectedForFirm
            ? vm.mission.inspectedForFirm.thirdPartyId
            : undefined,
        }).then(response => {

          let assetsGeojson = Object.freeze(response.data);
          vm.assetsGeojson = assetsGeojson;
        })
        .catch(err => {
          console.log(err);
        });
    },
    fetchAssetsForMovement: _.debounce(
      function() {
        console.log('[fetchAssetsForMovement]');
        const vm = this;
        return vm.fetchAssetsAroundCenter();
      },
      5000,
      { leading: true }
    ),
    updateMapLayers: _.debounce(
      function () {
        const vm = this;
        vm.updateMapLayersNow();
      },
      300,
      { trailing: true }
    ),
    updateMapLayersNow() {
      const vm = this;
      const mapboxComponent = vm.$refs.mapbox;
      if ( ! mapboxComponent) return;
      if ( ! vm.cpitGeojsonSortedByProximity) return;
      const map = vm.$refs.mapbox.getMapInstance();
      let assetLayer = map.getLayer("cpit-layer");
      let assetSource = map.getSource("cpit-source");
      if (assetSource) {
        assetSource.setData(vm.cpitGeojsonSortedByProximity);
      }
      else {
        map.addSource('cpit-source', {
          type: 'geojson',
          data: vm.cpitGeojsonSortedByProximity,
        });
      }
      if ( ! assetLayer) {
        /*
        // Default "circle" for Point features
        map.addLayer({
          source: 'cpit-source',
          id: 'cpit-layer',
          type: 'circle',
          'paint': {
            'circle-radius': 5,
            'circle-opacity': 0.8,
            // 'circle-color': '#444444',
            'circle-stroke-width': 1,
            'circle-stroke-color': '#000000',
            'circle-color': [
              'match',
              ['get', 'proximity'],
              1,
              vm.getProximityColor(1),
              2,
              vm.getProximityColor(2),
              3,
              vm.getProximityColor(3),
              // other
              vm.getProximityColor(undefined)
            ]
          },
        });
        */
        // Since we have converted our Points to Polygons we use "fill".
        /*
        map.addLayer({
          source: 'cpit-source',
          id: 'cpit-layer',
          type: 'fill',
          'paint': {
            'fill-outline-color': '#000000',
            'fill-opacity': 0.7,
            'fill-color': [
              'match',
              ['get', 'proximity'],
              1,
              vm.getProximityColor(1),
              2,
              vm.getProximityColor(2),
              3,
              vm.getProximityColor(3),
              // other
              vm.getProximityColor(undefined)
            ]
          },
        });
        */
        map.addLayer({
          source: 'cpit-source',
          id: 'cpit-layer',
          type: 'fill',
          'paint': {
            'fill-outline-color': '#000000',
            'fill-opacity': 1,
            'fill-color': [
              'match',
              ['get', 'marker_profile'],
              'default', vm.getColorByProfile('default'),
              // Below, the 0-1-2-x are close (2), closer (1), closest (0) and close-but-not-significant (x)
              'nearby-0', vm.getColorByProfile('nearby-0'),
              'nearby-1', vm.getColorByProfile('nearby-1'),
              'nearby-2', vm.getColorByProfile('nearby-2'),
              'nearby-x', vm.getColorByProfile('nearby-x'),
              'danger-0', vm.getColorByProfile('danger-0'),
              'danger-1', vm.getColorByProfile('danger-1'),
              'danger-2', vm.getColorByProfile('danger-2'),
              'danger-x', vm.getColorByProfile('danger-x'),
              'success-0', vm.getColorByProfile('success-0'),
              'success-1', vm.getColorByProfile('success-1'),
              'success-2', vm.getColorByProfile('success-2'),
              'success-x', vm.getColorByProfile('success-x'),
              // other
              vm.getColorByProfile('default'),
            ]
          },
        });

        // NOTE: We only add these listeners the first time the layer is added
        // (otherwise we need to use the map.off(...) event remover method)
        // Handle clicks on the CPIT layer features
        map.off('click', 'cpit-layer', vm.handleCpitFeatureTouchOrClick);
        map.off('touchstart', 'cpit-layer', vm.handleCpitFeatureTouchOrClick);
        map.on('click', 'cpit-layer', vm.handleCpitFeatureTouchOrClick);
        map.on('touchstart', 'cpit-layer', vm.handleCpitFeatureTouchOrClick);
        // Switch cursor when over the CPIT layer features
        // This is only useful for non-touch devices and
        // will also only take effect when not covered
        // by the semi-transparent overlay that gets
        // placed over the map while tracking user.
        map.on('mouseenter', 'cpit-layer', vm.handleCpitFeatureMouseEnter);
        map.on('mouseleave', 'cpit-layer', vm.handleCpitFeatureMouseLeave);
      }
    },
    handleCpitFeatureTouchOrClick(event) {
      if (event.type == 'touchstart') {
        // Prevent subsquent events like click, scrolling, zooming etc.
        // (touchstart imperfect but fine until better touch support)
        event.preventDefault();
      }
      let asset = _.get(event, 'features.0.properties');
      if (asset) this.selectAsset(asset);
    },
    handleCpitFeatureMouseEnter(event) {
      event.target.getCanvas().style.cursor = 'pointer';
    },
    handleCpitFeatureMouseLeave(event) {
      event.target.getCanvas().style.cursor = '';
    },
    isAssetDisabled(id) {
      return this.assetsDisabled.includes(id);
    },
    selectAsset(asset, isNew = false) {
      // [2021-08-06]
      // We now offer a dialog to void a mission-asset if an
      // asset that has already been inspected is clicked.
      // if (this.isAssetDisabled(asset.asset_id)) return;
      this.$emit("select-asset", { ...asset, isNew });
    },
    selectAssetById(id) {
      if (this.cpitGeojsonSortedByProximity) {
        let asset;
        const features = this.cpitGeojsonSortedByProximity
          ? this.cpitGeojsonSortedByProximity.features
          : [];
        features.forEach(f => {
          if (f.properties.asset_id == id) {
            asset = Object.assign({}, f.properties);
          }
        });
        if (asset) this.selectAsset(asset);
      }
    },
    setMapHeight() {
      let footerHeight = 0;

      if (this.$refs.footer) {
        footerHeight = this.$refs.footer.clientHeight;
      }

      // this.map.height = this.height - 24 - footerHeight; // col padding
      this.mapHeight = this.height - 24 - footerHeight; // col padding
    },
    onNewReferencePoint(coords) {
      this.lastReferencePoint = coords;
      this.$emit('reference-point', coords);
    },
    onNewUserLocation(coords) {
      this.lastUserLocation = coords;
      this.$emit('user-location', coords);
    },
    onUserTrackingChange(isTracking) {
      this.isTrackingUserLocation = isTracking;
    },
    onDrawingCreated(event) {
      // We're not using this one at the moment because the draw.selectionchange
      // event fires immediately afterwards and we need to listen to that one
      // too because the user may still move their drawing after creation.
      // We need the drawing's final coordinates during asset creation.
    },
    onDrawingUpdated(event) {
      this.handleCpitDrawingEvent(event);
    },
    onDrawingDeleted(event) {
      this.handleCpitDrawingEvent(event);
    },
    onDrawingSelectionChanged(event) {
      this.handleCpitDrawingEvent(event);
    },
    onDrawingModeChanged(event) {
      if (["draw_point"].includes(event.mode)) {
        this.drawingInProgress = true;
      }
    },
    getProximityColor(proximity_number) {
      let color = '#444444';
      if (proximity_number == 1) color = 'red';
      if (proximity_number == 2) color = 'orange';
      if (proximity_number == 3) color = 'cyan';
      return color;
    },
    getColorByProfile(profile) {
      return this.colors.hasOwnProperty(profile) ? this.colors[profile] : this.colors['default'];
    },
    getFeatureColor(feature) {
      let profile = _.get(feature, 'properties.marker_profile', 'default');
      return this.getColorByProfile(profile);
    },
    isCpitDrawingEvent(event) {
      const type = _.get(event, 'features.0.type');
      const geometry_type = _.get(event, 'features.0.geometry.type');
      const coordinates = _.get(event, 'features.0.geometry.coordinates');
      return (
        (type == 'Feature')
        &&
        (geometry_type == 'Point')
        &&
        (Array.isArray(coordinates) && coordinates.length == 2) // lng, lat
      );
    },
    cancelDrawing() {
      this.$refs.mapbox.drawControl.deleteAll();
      this.form.geometryType = '';
      this.form.nodes = [];
      this.drawingInProgress = false;
    },
    handleCpitDrawingEvent(event) {
      if (this.isCpitDrawingEvent(event)) {
        const event_type = _.get(event, 'type')
        if (event_type == 'draw.delete') {
          this.cancelDrawing();
        }
        else {
          const draw_id = _.get(event, 'features.0.id');
          const coordinates = _.get(event, 'features.0.geometry.coordinates');
          this.form.geometryType = 'Point';
          this.form.nodes = [{ longitude: coordinates[0], latitude: coordinates[1] }];
        }
      }
    },
    onMapStyleChange() {
      // When a user switches between styles (streets, satellite, ...) all assets disappear from map
      // Re-add sources/layers on each style change
      this.updateMapLayers();
    },
    getAssetButtonInnerHtml(asset) {
      let text_lines = [];
      const show_third_party_code = this.getConfigByKey('mission_map.asset_buttons_show_third_party_code');
      const show_last_service_date = this.getConfigByKey('mission_map.asset_buttons_show_last_service_date');
      if (show_third_party_code && asset.asset_third_party_code) {
        text_lines.push(asset.asset_third_party_code);
      }
      if (show_last_service_date) {
        const ma = _.get(this.lastServiceDataByCoreId, asset.asset_id);
        if (ma) {
          // Template
          /*
           * LAST SERVICE
           * ATTEMPTED: 01-JAN-2023
           * COMPLETED: YES/NO
           * (or "No service data found")
           */
          text_lines.push('LAST SERVICE');
          let attempted_at = this.$options.filters.dateLocal(ma.createdAt, 'DD-MMM-YYYY');
          let completed = ma.passFail === 'pass' ? 'YES' : 'NO';
          if (attempted_at) {
            text_lines.push(`ATTEMPTED: ${attempted_at}`);
          }
          text_lines.push(`COMPLETED: ${completed}`);
          text_lines = text_lines.map(line => {
            return _.escape(line);
          });
        }
        else if (_.isNull(ma)) {
          // Only show "No service data found" if we've actually looked for it.
          // (e.g. a null value for a core_id in this.lastServiceDataByCoreId)
          text_lines.push('No service data found');
        }
      }
      return text_lines.join('<br>');
    },
    getMissionAssetViaCoreId(coreId) {
      return new Promise((resolve, reject) => {
        MissionAssetService.all({ core_id: coreId, group_by: 'core_id', order_by: '-created_at' })
        .then(res => {
          let missionAssets = denormalize(res.data); // array since we're using the index endpoint
          let ma = _.head(missionAssets);
          resolve(ma);
        })
        .catch(err => reject(err));
      });
    },
    getMissionAssetsViaCoreIds(core_ids) {
      const vm = this;
      return new Promise(resolve => {
        let promises = [];
        core_ids.forEach(core_id => {
          promises.push(this.getMissionAssetViaCoreId(core_id));
        });
        Promise.allSettled(promises)
        .then(results => {
          let mission_assets = [];
          results.forEach(result => {
            const { status, value, reason } = result;
            if (value) {
              mission_assets.push(value);
            }
          });
          resolve(mission_assets);
        });
      });
    },
    getLastServiceDataByCoreId(core_id, initiated_at) {
      // console.log('core_id', core_id);
      // Loosely based on discussion between CD and RN:
      // https://sitevitals.slack.com/archives/DAJFVEULQ/p1687748614792399
      const vm = this;
      const now = Date.now();
      initiated_at = ( ! initiated_at) ? Date.now() : initiated_at;
      if (now - initiated_at > 10000) return; // start giving up on ones that have waited ages (vehicle moved on).
      if ( ! this.isMounted) return;
      const is_busy = this.lastServiceDataByCoreIdBusyFetching;
      const is_still_missing_data = ! this.lastServiceDataByCoreId.hasOwnProperty(core_id);
      if ( ! is_busy && is_still_missing_data) {
        let missionAsset;
        const start = Date.now();
        vm.lastServiceDataByCoreIdBusyFetching = true;
        vm.getMissionAssetViaCoreId(core_id)
          .then(ma => {
            missionAsset = ma;
          })
          .catch(err => {
            // For now swallow error in case this core_id fails repeatedly.
            // Otherwise need some way to retry and eventually give up?
          })
          .finally(() => {

            // @todo - keep stash of service data to a reasonable size so it doesn't grow massive
            // Can't loop through vm.lastServiceDataByCoreId keys because core_id is numeric and
            // so they are looped through in ASC order, not in the order added to the object.
            // Can use assetIdsInScopeForLastServiceData as long is it is still around.
            /*
            if (Object.keys(vm.lastServiceDataByCoreId).length > 30) {
              let resizedServiceDataByCoreId = {};
              for (let [core_id, ma] of Object.entries(vm.lastServiceDataByCoreId)) {
                core_id = parseInt(core_id, 10);
                if (vm.assetIdsInScopeForLastServiceData.indexOf(core_id) >= 0) {
                  resizedServiceDataByCoreId[core_id] = ma;
                }
              }
              vm.lastServiceDataByCoreId = resizedServiceDataByCoreId;
            }
            */

            // Reminder: missionAsset could have been empty if no item was found with core_id
            // or if an error was encountered then we just treat as no service data found
            // so that this process won't keep retrying/failing on troublesome core_id.
            vm.$set(
              vm.lastServiceDataByCoreId,
              core_id,
              missionAsset || null
            );

            // Calculate time to keep rate to ~1 req/second.
            const delay = (Date.now() - start) < 1000 ? 1000 - (Date.now() - start) : 0
            // console.log('Delaying for milliseconds:', delay);
            window.setTimeout(
              function () {
                vm.lastServiceDataByCoreIdBusyFetching = false;
              },
              delay
            );
          });
      }
      else {
        window.setTimeout(vm.getLastServiceDataByCoreId, 100, core_id, initiated_at);
      }
    }
  }
};
</script>

<style>
  .mapbox-container {
    position: relative;
    width: 100%;
  }
  /* The .mapbox-center-marker is for when using a centered map overlay icon */
  .mapbox-center-marker {
    position: absolute;
    pointer-events: none;
    top: calc(50% - 30px);
    left: calc(50% - 30px);
    width: 60px;
    height: 60px;
    background-image: url('/truck.svg');
    background-size: contain;
    background-position: top center;
    transform: rotate(270deg);
    z-index: 111;
  }
  /* The .mapbox-truck-marker is for when using a custom icon for mapbox's location marker */
  /* Adapted from: https://codepen.io/avstorm/pen/ClguF */
  .mapbox-truck-marker {
    width: 60px;
    height: 60px;
  }
  .mapbox-truck-marker:before {
    content: "";
    width: 60px;
    height: 60px;
    background-image: url('/truck.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: top center;
    position: absolute;
    margin-left: 0px;
    transform: rotate(270deg);
  }
  .mapbox-truck-marker.is-tracking:after {
    content: "";
    border-radius: 50%;
    height: 60px;
    width: 60px;
    position: absolute;
    animation: pulsate 1.2s ease-out;
    animation-iteration-count: infinite;
    opacity: 0.0;
    box-shadow: 0 0 1px 2px #1976d2;
    margin: -0px 0 0 -12px; /* no idea why 12px but it works */
    animation-delay: .2s;
    z-index: -1;
  }

  @keyframes pulsate {
  0% {
    transform: scale(0.1, 0.1);
    opacity: 0.0;
  }
  50% {
    opacity: 1.0;
  }
  100% {
    transform: scale(1.2, 1.2);
    opacity: 0;
  }
}
</style>
